import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { t } from "ttag";

import { ErrorModal } from "@/app/components/legacy-modals";
import { referralQueriesStore } from "@/features/referral/api";
import { ReferralStats } from "@/features/referral/components";
import {
  useApplyReferralCreditAmount,
  useReferralCode,
} from "@/features/referral/hooks";
import type { CreditAmount } from "@/features/referral/models";
import { CreditOptions } from "@/features/referral/sections";
import { LogoLoader } from "@/shared/components";
import { REFERRAL_CREDIT_AMOUNT } from "@/shared/domains/referral";
import type { ModalProps } from "@/shared/models/legacy-types";
import { Button, Dialog, LightbulbIcon } from "@/shared/ui";

export const ReferralDashboardModal = ({ show, onClose }: ModalProps) => {
  const [selectedAmount, setSelectedAmount] = useState<CreditAmount>();

  const {
    data: credit,
    isLoading,
    isError,
    isSuccess,
  } = useQuery(referralQueriesStore.credit);

  const {
    actions: { shareCode },
  } = useReferralCode();

  const applyCreditAmountMutation = useApplyReferralCreditAmount();

  const canApplyCreditAmount =
    isSuccess && credit.available > 0 && !credit.hasReachedMonthlyLimit;

  const handleApplyCreditAmount = () => {
    if (canApplyCreditAmount && selectedAmount) {
      return applyCreditAmountMutation.mutate(selectedAmount, {
        onSuccess: onClose,
      });
    }
  };

  if (isError) {
    return <ErrorModal open={show} onOpenChange={onClose} />;
  }

  return (
    <Dialog.Root open={show} onOpenChange={onClose}>
      <LogoLoader
        show={(isLoading || applyCreditAmountMutation.isPending) && show}
        withOverlay
        fullscreen={applyCreditAmountMutation.isPending}
      />

      {isSuccess && (
        <Dialog.Content>
          <Dialog.Header icon={LightbulbIcon}>
            <Dialog.Title>{t`Referral dashboard`}</Dialog.Title>
            <Dialog.Description>
              {t`Give $${REFERRAL_CREDIT_AMOUNT}, get $${REFERRAL_CREDIT_AMOUNT}! Invite friends & family to join LevelsRx!`}
            </Dialog.Description>
          </Dialog.Header>

          <ReferralStats credit={credit} />

          <CreditOptions
            credit={credit}
            value={selectedAmount}
            onChange={setSelectedAmount}
          />

          <Dialog.Footer>
            <Button
              size="lg"
              className="grow"
              onClick={
                canApplyCreditAmount ? handleApplyCreditAmount : shareCode
              }
              disabled={canApplyCreditAmount && !selectedAmount}
            >
              {canApplyCreditAmount ? t`Apply` : t`Share code`}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
};
